import React, { useEffect, useState, useMemo } from "react";
import { Pagination } from "./Pagination";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { CircuitBoard, RefreshCw, Search, ChevronUp, ChevronDown, X } from "lucide-react";

interface PCIDevicesProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const DEFAULT_PER_PAGE = 10;

const PCIDevices: React.FC<PCIDevicesProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [allDevices, setAllDevices] = useState<any[]>([]);
    const [devices, setDevices] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [searchInput, setSearchInput] = useState<string>("");
    const [loading, setLoading] = useState(true);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Fetch PCI Devices (only once)
    async function fetchDevices() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ client_id, organization_id, agent_id, asset_id }),
            };
            const response = await baseapi.get("/asset_pci_devices", { params });
            const resData = response.data;
            let rawList: any[] = [];
            if (Array.isArray(resData?.data)) {
                rawList = resData.data;
            } else if (Array.isArray(resData)) {
                rawList = resData;
            } else if (resData?.data?.[0]?.results) {
                const raw = resData.data[0].results;
                rawList = typeof raw === "string" ? JSON.parse(raw) : raw ?? [];
            } else if (resData?.results) {
                const raw = resData.results;
                rawList = typeof raw === "string" ? JSON.parse(raw) : raw ?? [];
            }

            const normalized = (Array.isArray(rawList) ? rawList : []).map((item: any) => {
                const busDevFunc = item.bus_dev_func ?? item["Bus:Dev:Func"] ?? item.busDevFunc ?? "—";
                const description = item.description ?? item.Description ?? "—";
                const location = item.location ?? item.Location ?? "—";
                const vendor = item.vendor ?? item.Vendor ?? "—";
                return {
                    ...item,
                    bus_dev_func: busDevFunc,
                    description: description,
                    location: location,
                    vendor: vendor,
                    "Bus:Dev:Func": busDevFunc,
                    Description: description,
                    Location: location,
                    Vendor: vendor,
                };
            });
            setAllDevices(normalized);
        } catch (err: any) {
            showError(err.message);
            setAllDevices([]);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchDevices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id]);

    // Filter and Sort devices locally
    const filteredAndSorted = useMemo(() => {
        let result = allDevices;

        // Apply Search Filter
        const term = searchInput.trim().toLowerCase();
        if (term) {
            result = result.filter((device) => {
                if (!device) return false;
                return Object.values(device).some((val) => {
                    if (val === null || val === undefined) return false;
                    if (typeof val === "object") {
                        return Object.values(val).some(v => v && String(v).toLowerCase().includes(term));
                    }
                    return String(val).toLowerCase().includes(term);
                });
            });
        }

        // Apply Sorting
        if (sortConfig) {
            result = [...result].sort((a, b) => {
                const aValue = a[sortConfig.key] ? String(a[sortConfig.key]).toLowerCase() : "";
                const bValue = b[sortConfig.key] ? String(b[sortConfig.key]).toLowerCase() : "";

                if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [allDevices, searchInput, sortConfig]);

    // Pagination calculation
    useEffect(() => {
        const offset = (page - 1) * perPage;
        setDevices(filteredAndSorted.slice(offset, offset + perPage));
    }, [filteredAndSorted, page, perPage]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(filteredAndSorted.length / perPage)) {
            setPage(newPage);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass }: { label: string; sortKey: string; widthClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 cursor-pointer group select-none`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    return (
        <div className="p-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <CircuitBoard className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">PCI Devices</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {filteredAndSorted.length} devices found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            setSearchInput("");
                            setPage(1);
                            fetchDevices();
                        }}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>

                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                setPage(1);
                            }}
                            placeholder="Search PCI devices..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                        {searchInput && (
                            <button
                                onClick={() => {
                                    setSearchInput("");
                                    setPage(1);
                                }}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                title="Clear search"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden">
                <table className="w-full table-fixed">
                    <thead className="bg-gray-800/80">
                        <tr>
                            <SortableHeader widthClass="w-[15%]" label="Bus:Dev:Func" sortKey="bus_dev_func" />
                            <SortableHeader widthClass="w-[45%]" label="Description" sortKey="description" />
                            <SortableHeader widthClass="w-[20%]" label="Location" sortKey="location" />
                            <SortableHeader widthClass="w-[20%]" label="Vendor" sortKey="vendor" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {loading ? (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-gray-200 text-[11px]">Loading devices...</td>
                            </tr>
                        ) : devices.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-gray-200 text-[11px]">No PCI devices found</td>
                            </tr>
                        ) : (
                            devices.map((dev, idx) => (
                                <tr key={dev.id ? `dev-${dev.id}` : `${dev.bus_dev_func}-${idx}`} className="hover:bg-gray-800/40 transition-colors duration-100">
                                    <td className="px-3 py-2 text-white font-mono text-[11px] truncate" title={dev.bus_dev_func}>{dev.bus_dev_func}</td>
                                    <td className="px-3 py-2 text-gray-300 text-[11px] truncate" title={dev.description}>{dev.description}</td>
                                    <td className="px-3 py-2 text-[10px] text-gray-200 truncate" title={dev.location}>{dev.location}</td>
                                    <td className="px-3 py-2 text-[10px] text-gray-200 truncate" title={dev.vendor}>{dev.vendor}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot className="bg-gray-900/60">
                        <tr>
                            <td colSpan={4} className="p-0">
                                <Pagination
                                    currentPage={page}
                                    totalPages={Math.max(1, Math.ceil(filteredAndSorted.length / perPage))}
                                    onPageChange={handlePageChange}
                                    itemsPerPage={perPage}
                                    totalItems={filteredAndSorted.length}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default PCIDevices;